<script setup lang="ts">
import {AvailableTemplates, formatDate} from "~/utils/misc";
import type {Story} from "~/types/story";

const props = withDefaults(
    defineProps<{
      story: Story,
      isShowcase?: boolean,
      isCreateNew?: boolean
    }>(), {
      isShowcase: false,
      isCreateNew: false
    }
)

function getRandomTemplate() {
  return AvailableTemplates[Math.floor(Math.random() * AvailableTemplates.length)];
}

function openPage(id: string | null) {
  if (!id) {
    return;
  }
  const query = {
    id: id,
    ...(props.isShowcase && { t: getRandomTemplate() })
  };

  const localePath = useLocalePath();
  const fullPath = localePath({
    path: '/view',
    query: query
  });
  if (import.meta.client) {
    window.open(fullPath, '_blank');
  }
}
</script>

<template>
  <div class="main-container col-xl-4 col-md-6  mb-xl-0 mb-5">
    <div class="img-container">
      <img :src="getStoryImage(story)" :alt="story.firstName">
    </div>
    <div class="details px-0 pt-4 pb-2">
      <span class="icon sample-story-link" @click="openPage(story.uuid)" v-if="!props.isCreateNew">
        <i class="bi bi-eye-fill"></i>
      </span>
      <div class="details-name-container">
        <h3 class="sample-story-link" v-if="props.isCreateNew">
          <span>{{ $t('stories.card.createNewStory') }}</span>
        </h3>
        <div v-else>
          <h3 @click="openPage(story.uuid)" class="sample-story-link">
            <span>{{ story.firstName }} {{ story.lastName }}</span>
          </h3>
          <small v-if="!isShowcase && !story.unlimited">{{ $t('stories.card.expiresOn') }} {{ formatDate(story.validUntilDate) }}</small>
        </div>
      </div>
      <div class="container p-0 m-0">
        <div class="justify-content-center" v-if="props.isCreateNew">
          <NuxtLinkLocale :to="{ path: '/add-story'}" class="btn p-1 btn-warning col-4 m-1">{{ $t('stories.card.create') }}</NuxtLinkLocale>
        </div>
        <div class="d-flex justify-content-evenly" v-else-if="!props.isShowcase">
          <NuxtLinkLocale :to="{ path: '/add-story', query: { id: story.uuid } }" class="btn p-1 btn-warning col-4">{{ $t('stories.card.edit') }}</NuxtLinkLocale>
          <NuxtLinkLocale :to="{ path: '/qrcodes', query: { id: story.uuid } }" class="btn p-1 btn-warning col-4">{{ $t('stories.card.qrCode') }}</NuxtLinkLocale>
          <NuxtLinkLocale v-if="!story.unlimited" :to="{ path: '/validity', query: { id: story.uuid } }" class="btn p-1 btn-warning col-4">{{ $t('stories.card.extend') }}</NuxtLinkLocale>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>

.img-container {
  min-width: 300px;
  border-radius: 10px;
  overflow: hidden;
}

img {
  width: 100%;
  height: auto;
  aspect-ratio: 1 / 1;
  object-fit: cover;
  box-sizing: border-box;
  border-radius: 10px;
  border: 1px solid lightgrey;
  transition: 0.6s;
}

img:hover {
  transform: scale(1.2);
}

.details:hover h3 {
  color: var(--accent-color);
}

.details .icon:hover {
  background: var(--surface-color);
  border: 2px solid var(--accent-color);
}

.details .icon:hover i {
  color: var(--accent-color);
}

h3 {
  font-weight: 700;
  margin: 0;
  font-size: 22px;
  transition: ease-in-out 0.3s;
}

p {
  line-height: 24px;
  font-size: 14px;
  margin-bottom: 0;
}

.main-container:hover {
  background: var(--background-color);
}

.main-container:hover h3 {
  color: var(--accent-color);
  cursor: pointer;
}

.icon:hover {
  background: var(--surface-color);
  border: 2px solid var(--accent-color);
  cursor: pointer;
}

.icon:hover i {
  color: var(--accent-color);
}

.details {
  background: color-mix(in srgb, var(--surface-color), transparent 5%);
  padding: 50px 30px;
  margin: -25px 10px 0 10px;
  transition: all ease-in-out 0.3s;
  position: relative;
  text-align: center;
  border-radius: 8px;
  border: 1px solid lightgrey;
  box-shadow: 0 0 25px rgba(0, 0, 0, 0.1);
}

.icon {
  --size: 50px;
  width: var(--size);
  height: var(--size);
  background: var(--accent-color);
  color: var(--contrast-color);
  border: 4px solid var(--contrast-color);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;
  font-size: 28px;
  transition: ease-in-out 0.3s;
  position: absolute;
  top: calc(var(--size) / -2);
  left: calc(50% - (var(--size) / 2));
}

.btn {
  width: 100px;
}

.details-name-container {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-height: 50px;
}
</style>